import React, { useState, useEffect } from 'react';
import api from './api';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaBell, FaUsers, FaInfoCircle, FaSearch } from 'react-icons/fa';

const Messaging = () => {
    const [popupMessage, setPopupMessage] = useState('');
    const [notification, setNotification] = useState({ title: '', message: '', type: 'info' });
    const [popupTargetLicense, setPopupTargetLicense] = useState('all');
    const [notificationTargetLicense, setNotificationTargetLicense] = useState('all');
    const [activeLicenses, setActiveLicenses] = useState([]);
    const [messagesHistory, setMessagesHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [popupSearch, setPopupSearch] = useState('');
    const [notificationSearch, setNotificationSearch] = useState('');

    useEffect(() => {
        fetchLicenses();
        fetchMessages();
    }, []);

    const fetchLicenses = async () => {
        try {
            const { data } = await api.get('/licenses');
            setActiveLicenses(data);
        } catch (err) { }
    };

    const fetchMessages = async () => {
        try {
            const { data } = await api.get('/messages');
            setMessagesHistory(data);
        } catch (err) { }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm('Delete this message record?')) return;
        try {
            await api.delete(`/messages/${id}`);
            toast.success('Message deleted from history');
            fetchMessages();
        } catch (err) {
            toast.error('Failed to delete message');
        }
    };

    const sendPopup = async (e) => {
        e.preventDefault();
        if (!popupMessage) return toast.error('Message cannot be empty');
        setLoading(true);
        try {
            await api.post('/messages/send-popup', { 
                license_key: popupTargetLicense, 
                message: popupMessage 
            });
            toast.success('Popup message sent!');
            setPopupMessage('');
            fetchMessages();
        } catch (err) {
            toast.error('Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    const sendNotification = async (e) => {
        e.preventDefault();
        if (!notification.message) return toast.error('Notification message cannot be empty');
        setLoading(true);
        try {
            await api.post('/messages/send-notification', { 
                ...notification,
                license_key: notificationTargetLicense
            });
            toast.success(`System notification ${notificationTargetLicense === 'all' ? 'broadcasted' : 'sent to ' + notificationTargetLicense}`);
            setNotification({ title: '', message: '', type: 'info' });
            fetchMessages();
        } catch (err) {
            toast.error('Failed to send notification');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="module">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <FaPaperPlane style={{ color: '#66fcf1' }} /> Messaging & Notifications
            </h2>
            <p style={{ color: '#aaa', marginBottom: '30px' }}>
                Communicate with your customers in real-time. Choose between direct popup messages or global system notifications.
            </p>

            <div className="charts-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                
                {/* Popup Message Form */}
                <div className="module" style={{ background: 'rgba(31, 40, 51, 0.4)', marginBottom: 0 }}>
                    <h3><FaUsers style={{ color: '#45a29e', marginRight: '10px' }} /> Direct Popup Message</h3>
                    <p style={{ fontSize: '0.85rem', color: '#888' }}>Sends a modal popup that must be dismissed by the customer.</p>
                    <form onSubmit={sendPopup} style={{ marginTop: '20px' }}>
                        <label>Target License</label>
                        <div style={{ position: 'relative', marginBottom: '10px' }}>
                            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#45a29e', fontSize: '0.8rem' }} />
                            <input 
                                placeholder="Search license..." 
                                value={popupSearch}
                                onChange={e => setPopupSearch(e.target.value)}
                                style={{ padding: '8px 10px 8px 30px', width: '100%', fontSize: '0.8rem', background: '#0b0c10', border: '1px solid #45a29e', borderRadius: '4px', color: '#fff' }}
                            />
                        </div>
                        <select 
                            value={popupTargetLicense} 
                            onChange={e => setPopupTargetLicense(e.target.value)}
                            style={{ width: '100%', marginBottom: '15px', background: '#0b0c10', color: '#fff', border: '1px solid #45a29e', padding: '10px', borderRadius: '4px' }}
                        >
                            <option value="all">Broadcast to All Online</option>
                            {activeLicenses
                                .filter(l => 
                                    l.customer_name.toLowerCase().includes(popupSearch.toLowerCase()) || 
                                    l.license_key.toLowerCase().includes(popupSearch.toLowerCase())
                                )
                                .map(l => (
                                    <option key={l.id} value={l.license_key}>{l.customer_name} ({l.license_key})</option>
                                ))}
                        </select>

                        <label>Message Content</label>
                        <textarea 
                            rows="6" 
                            placeholder="Type your message here..."
                            value={popupMessage}
                            onChange={e => setPopupMessage(e.target.value)}
                            required
                            style={{ 
                                width: '100%', 
                                background: '#0b0c10', 
                                color: '#fff', 
                                border: '1px solid #45a29e', 
                                padding: '12px',
                                borderRadius: '4px',
                                fontSize: '0.95rem',
                                resize: 'vertical',
                                minHeight: '120px'
                            }}
                        />

                        <button disabled={loading} style={{ width: '100%', marginTop: '10px' }}>
                            <FaPaperPlane /> {loading ? 'Sending...' : 'Send Popup'}
                        </button>
                    </form>
                </div>

                {/* System Notification Form */}
                <div className="module" style={{ background: 'rgba(31, 40, 51, 0.4)', marginBottom: 0 }}>
                    <h3><FaBell style={{ color: '#66fcf1', marginRight: '10px' }} /> Global Notification</h3>
                    <p style={{ fontSize: '0.85rem', color: '#888' }}>Displays a brief toast notification to all active application users.</p>
                    <form onSubmit={sendNotification} style={{ marginTop: '20px' }}>
                        <label>Target License</label>
                        <div style={{ position: 'relative', marginBottom: '10px' }}>
                            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#45a29e', fontSize: '0.8rem' }} />
                            <input 
                                placeholder="Search license..." 
                                value={notificationSearch}
                                onChange={e => setNotificationSearch(e.target.value)}
                                style={{ padding: '8px 10px 8px 30px', width: '100%', fontSize: '0.8rem', background: '#0b0c10', border: '1px solid #45a29e', borderRadius: '4px', color: '#fff' }}
                            />
                        </div>
                        <select 
                            value={notificationTargetLicense} 
                            onChange={e => setNotificationTargetLicense(e.target.value)}
                            style={{ width: '100%', marginBottom: '15px', background: '#0b0c10', color: '#fff', border: '1px solid #45a29e', padding: '10px', borderRadius: '4px' }}
                        >
                            <option value="all">Broadcast to All Online</option>
                            {activeLicenses
                                .filter(l => 
                                    l.customer_name.toLowerCase().includes(notificationSearch.toLowerCase()) || 
                                    l.license_key.toLowerCase().includes(notificationSearch.toLowerCase())
                                )
                                .map(l => (
                                    <option key={l.id} value={l.license_key}>{l.customer_name} ({l.license_key})</option>
                                ))}
                        </select>

                        <label>Notification Title</label>
                        <input 
                            placeholder="e.g. System Maintenance"
                            value={notification.title}
                            onChange={e => setNotification({ ...notification, title: e.target.value })}
                            style={{ width: '100%', marginBottom: '15px', background: '#0b0c10', color: '#fff', border: '1px solid #45a29e', padding: '10px', borderRadius: '4px' }}
                        />

                        <label>Notification Type</label>
                        <select 
                            value={notification.type}
                            onChange={e => setNotification({ ...notification, type: e.target.value })}
                            style={{ width: '100%', marginBottom: '15px', background: '#0b0c10', color: '#fff', border: '1px solid #45a29e', padding: '10px', borderRadius: '4px' }}
                        >
                            <option value="info">Information</option>
                            <option value="warning">Warning</option>
                            <option value="error">Critical Error</option>
                            <option value="tray">Tray Notification (Sync via Heartbeat)</option>
                        </select>

                        <label>Message Content</label>
                        <textarea 
                            rows="4" 
                            placeholder="Brief notification summary..."
                            value={notification.message}
                            onChange={e => setNotification({ ...notification, message: e.target.value })}
                            required
                            style={{ 
                                width: '100%', 
                                background: '#0b0c10', 
                                color: '#fff', 
                                border: '1px solid #45a29e', 
                                padding: '12px',
                                borderRadius: '4px',
                                fontSize: '0.95rem',
                                resize: 'vertical'
                            }}
                        />

                        <button disabled={loading} style={{ width: '100%', marginTop: '10px', background: '#45a29e' }}>
                            <FaBell /> {loading ? (notificationTargetLicense === 'all' ? 'Broadcasting...' : 'Sending...') : (notificationTargetLicense === 'all' ? 'Broadcast Notification' : 'Send Notification')}
                        </button>
                    </form>
                </div>

            </div>

            <div style={{ marginTop: '40px' }}>
                <h3 style={{ color: '#66fcf1' }}>Message History</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Target</th>
                                <th>Title / Label</th>
                                <th>Message</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {messagesHistory.map(m => (
                                <tr key={m.id}>
                                    <td>{new Date(m.created_at).toLocaleString()}</td>
                                    <td><span className={`status ${m.type === 'popup' ? 'blocked' : 'active'}`}>{m.type}</span></td>
                                    <td>{m.target_license === 'all' ? 'All Devices' : m.target_license}</td>
                                    <td>{m.title || '-'}</td>
                                    <td>{m.message}</td>
                                    <td>
                                        <button onClick={() => deleteMessage(m.id)} style={{ width: 'auto', padding: '5px 10px', background: 'transparent', border: '1px solid #ff4d4d', color: '#ff4d4d' }}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {messagesHistory.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', color: '#888' }}>No messages sent yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ marginTop: '40px', padding: '20px', background: 'rgba(102, 252, 241, 0.05)', borderRadius: '8px', borderLeft: '4px solid #66fcf1' }}>
                <h4 style={{ margin: 0, color: '#66fcf1' }}><FaInfoCircle /> Tips</h4>
                <ul style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '10px' }}>
                    <li>Popup messages are ideal for urgent alerts or policy changes.</li>
                    <li>Global notifications are less intrusive and better for general updates.</li>
                    <li><strong>Tray Notifications</strong> are picked up by the Windows tray icon via the health check (heartbeat) API.</li>
                    <li>Messages are only received by customers who are currently online.</li>
                </ul>
            </div>
        </div>
    );
};

export default Messaging;
